// SimpleAI - Basic AI opponent for testing game flow

import type { GameState, BattlefieldPosition, Card } from '../types';
import { TurnManager } from '../core/state/TurnManager';

export interface AIAction {
  type: 'draw' | 'summon' | 'attack' | 'move' | 'endPhase';
  cardId?: string;
  position?: BattlefieldPosition;
  attackerIds?: string[];
  delay: number;  // Delay in ms before executing
}

/**
 * Simple AI that makes basic decisions for player2.
 * Actions are executed with delays for visual feedback.
 */
export class SimpleAI {
  private playerId: string;

  constructor(playerId: string) {
    this.playerId = playerId;
  }

  /**
   * Determine the next action for the AI to take.
   */
  public getNextAction(state: GameState): AIAction | null {
    // Only act on AI's turn
    if (state.activePlayerId !== this.playerId) {
      return null;
    }

    const phase = state.currentPhase;
    const player = state.activePlayerId === state.player1.id ? state.player1 : state.player2;

    switch (phase) {
      case 'draw':
        // Auto-draw is handled by the phase handler
        return { type: 'endPhase', delay: 500 };

      case 'defense':
        // Auto-resolve defense
        return { type: 'endPhase', delay: 500 };

      case 'summon':
        return this.getSummonAction(state, player);

      case 'tactics':
        return this.getTacticsAction(state);

      case 'end':
        return { type: 'endPhase', delay: 300 };

      default:
        return null;
    }
  }

  /**
   * Get a summon action if possible.
   */
  private getSummonAction(state: GameState, player: typeof state.player1): AIAction | null {
    // Get cards in hand that can be summoned (units only)
    // For now, all cards in hand can be summoned
    const handCards = player.hand;

    if (handCards.length === 0) {
      return { type: 'endPhase', delay: 400 };
    }

    // Find empty slots on AI's side
    const emptySlots = this.getEmptySlots(state, this.playerId === state.player1.id ? 'player1' : 'player2');

    if (emptySlots.length === 0) {
      return { type: 'endPhase', delay: 400 };
    }

    // Pick a random card and random slot
    const cardToSummon = handCards[Math.floor(Math.random() * handCards.length)];

    // Prefer front row for units (simple strategy)
    const frontSlots = emptySlots.filter(s => s.row === 'front');
    const backSlots = emptySlots.filter(s => s.row === 'back');

    let targetSlot: BattlefieldPosition;
    if (frontSlots.length > 0) {
      targetSlot = frontSlots[Math.floor(Math.random() * frontSlots.length)];
    } else if (backSlots.length > 0) {
      targetSlot = backSlots[Math.floor(Math.random() * backSlots.length)];
    } else {
      return { type: 'endPhase', delay: 400 };
    }

    return {
      type: 'summon',
      cardId: cardToSummon.id,
      position: targetSlot,
      delay: 800,
    };
  }

  /**
   * Get a tactics action (attack or end phase).
   */
  private getTacticsAction(state: GameState): AIAction | null {
    // Get units on the battlefield that can attack
    const aiSide = this.playerId === state.player1.id ? 'player1' : 'player2';
    const myUnits = this.getUnitsOnSide(state, aiSide);

    // Filter units that haven't attacked (not tilted)
    const availableAttackers = myUnits.filter(unit => unit.orientation !== 'tilted');

    if (availableAttackers.length > 0 && Math.random() > 0.3) {
      // 70% chance to attack with all available units
      const attackerIds = availableAttackers.map(u => u.id);
      return {
        type: 'attack',
        attackerIds,
        delay: 1000,
      };
    }

    return { type: 'endPhase', delay: 600 };
  }

  /**
   * Get all empty battlefield slots for a player.
   */
  private getEmptySlots(state: GameState, side: 'player1' | 'player2'): BattlefieldPosition[] {
    const emptySlots: BattlefieldPosition[] = [];
    const playerSide = side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;

    for (const slot of playerSide.frontLine) {
      if (!slot.unit) {
        emptySlots.push(slot.position);
      }
    }

    for (const slot of playerSide.backLine) {
      if (!slot.unit) {
        emptySlots.push(slot.position);
      }
    }

    return emptySlots;
  }

  /**
   * Get all units on a player's side of the battlefield.
   */
  private getUnitsOnSide(state: GameState, side: 'player1' | 'player2'): Card[] {
    const units: Card[] = [];
    const playerSide = side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;

    for (const slot of playerSide.frontLine) {
      if (slot.unit) {
        units.push(slot.unit);
      }
    }

    for (const slot of playerSide.backLine) {
      if (slot.unit) {
        units.push(slot.unit);
      }
    }

    return units;
  }
}

/**
 * Execute an AI action on the game state.
 */
export function executeAIAction(
  action: AIAction,
  state: GameState
): { success: boolean; state?: GameState; error?: string } {
  switch (action.type) {
    case 'draw':
      return TurnManager.executeDraw(state);

    case 'summon':
      if (!action.cardId || !action.position) {
        return { success: false, error: 'Missing card or position' };
      }
      return TurnManager.executeSummon(state, action.cardId, action.position);

    case 'attack':
      if (!action.attackerIds || action.attackerIds.length === 0) {
        return { success: false, error: 'No attackers specified' };
      }
      return TurnManager.executeAttack(state, action.attackerIds);

    case 'endPhase':
      return TurnManager.endPhase(state);

    default:
      return { success: false, error: 'Unknown action type' };
  }
}
